// @flow
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type Ref,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { colors } from '../../constants';
import {
  outOfView,
  calculateYAdjustments,
  calculateXAdjustments,
} from '../../utils/out_of_view';
import _ from 'underscore';

type ModalStyles = {
  top: ?string,
  bottom: ?string,
  left: ?string,
  right: ?string,
  translateX: ?number,
  translateY: ?number,
};

type Direction = 'up' | 'down' | 'left' | 'right';

type ArrowStyles = {
  direction: ?Direction,
  top: ?string,
  bottom: ?string,
  left: ?string,
  right: ?string,
};

type Props = {
  wrapperRef: Ref<any>,
  rootId: string,
  modalStyles: ModalStyles,
  arrowStyles: ArrowStyles,
  children: (any) => ComponentType<any>,
  toggleOpen: () => void,
  open: boolean,
  noArrow: ?boolean,
};

const ARROW_HEIGHT = 5;

const ModalWrapper = styled.div`
  z-index: 2000;
  position: absolute;

  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  border: solid 1px ${colors.lightGrey};

  top: ${(props) => props.modalStyles.top};
  bottom: ${(props) => props.modalStyles.bottom};
  left: ${(props) => props.modalStyles.left};
  right: ${(props) => props.modalStyles.right};

  ${(props) => modalMarginStyles(props.arrowStyles.direction)}: '10px';

  transform: translate(
    ${(props) => props.modalStyles.translateX || 0}px,
    ${(props) => props.modalStyles.translateY || 0}px
  );

  &:before {
    display: ${(props) => (props.noArrow ? 'none' : 'block')};
    content: '';
    position: absolute;
    ${(props) => setDirection(props.arrowStyles.direction)}: calc(100% - 5px);
    top: ${(props) => props.arrowStyles.top};
    bottom: ${(props) => props.arrowStyles.bottom};
    left: ${(props) => props.arrowStyles.left};
    right: ${(props) => props.arrowStyles.right};
    height: 10px;
    width: 10px;
    background: white;
    transform: rotate(45deg);
    ${(props) => setBorder(props.arrowStyles.direction)}: box-shadow: inherit;
  }
`;

const PopoverModal = (props: Props) => {
  const {
    wrapperRef,
    children,
    toggleOpen,
    rootId,
    open,
    noArrow = false,
  } = props;

  const modalRoot = useRef(null);
  const el = document.createElement('div');
  const [modalStyles, setModalStyles] = useState(props.modalStyles || {});
  const [arrowStyles, setArrowStyles] = useState(props.arrowStyles || {});

  const ensureModalRendersInViewport = useCallback(() => {
    let newStyles = _.clone(modalStyles);
    let newArrowStyles = _.clone(arrowStyles);
    let [bounds, outside] = outOfView(el);

    const flipYDisplay = () => {
      const adjustments = calculateYAdjustments(
        modalRoot,
        bounds,
        outside,
        newStyles,
        ARROW_HEIGHT
      );

      if (adjustments == null) {
        return;
      }

      if (adjustments.translateY == null) {
        delete newStyles.translateY;
      } else {
        newStyles.translateY = adjustments.translateY;
      }
      newArrowStyles.direction = adjustments.direction;
    };

    const flipXDisplay = () => {
      const adjustments = calculateXAdjustments(outside, newStyles);

      if (adjustments == null) {
        return;
      }

      if (adjustments.translateX == null) {
        delete newStyles.translateX;
      } else {
        newStyles.translateX = adjustments.translateX;
      }
    };

    const assignNewStyles = () => {
      if (!_.isEqual(newStyles, modalStyles)) {
        setModalStyles(newStyles);
      }

      if (!_.isEqual(newArrowStyles, arrowStyles)) {
        setArrowStyles(newArrowStyles);
      }
    };

    flipYDisplay();
    flipXDisplay();
    assignNewStyles();
  }, [el, modalStyles, arrowStyles]);

  /* $FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.93.0. To view the error, delete this comment and run Flow. */
  useEffect(() => {
    modalRoot.current = document.getElementById(rootId);
    modalRoot.current && modalRoot.current.appendChild(el);
    ensureModalRendersInViewport();

    return () => modalRoot.current && modalRoot.current.removeChild(el);
  }, [el, rootId, ensureModalRendersInViewport, modalStyles, arrowStyles]);

  return open
    ? ReactDOM.createPortal(
        <ModalWrapper
          ref={wrapperRef}
          modalStyles={modalStyles}
          arrowStyles={arrowStyles}
          noArrow={noArrow}
        >
          {children({ closeModal: toggleOpen })}
        </ModalWrapper>,
        el
      )
    : null;
};

const modalMarginStyles = (direction) => {
  switch (direction) {
    case 'down':
      return 'margin-top';
    default:
      return 'margin-bottom';
  }
};

const setDirection = (direction) => {
  switch (direction) {
    case 'up':
      return 'bottom';
    case 'down':
      return 'top';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    default:
      return 'bottom';
  }
};

const setBorder = (direction) => {
  switch (direction) {
    case 'up':
      return 'border-top: inherit; border-left: inherit;';
    case 'down':
      return 'border-bottom: inherit; border-right: inherit;';
    case 'left':
      return 'border-bottom: inherit; border-left: inherit;';
    case 'right':
      return 'border-top: inherit; border-right: inherit;';
    default:
      return 'border-top: inherit; border-left: inherit;';
  }
};

export default PopoverModal;
